/**
 * The phone beside every step (spec §2.1).
 *
 * It is the REAL composition — the same `ClipComposition` the renderer runs —
 * over the committed sample clip, paused on the cue that shows what the
 * current step changes. Not a mock and not a screenshot: what the operator
 * judges here is what ships.
 *
 * The step choices arrive as `designPatch` and are replayed as ONE journal
 * entry ("Your choices so far"), which is the same entry the Adjust panel
 * opens on later — so the preview and the fine-tune editor can never disagree
 * about what the wizard decided.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Maximize2, Pause, Play, X } from "lucide-react";
import { Player, type PlayerRef } from "@remotion/player";
import { ClipComposition } from "../../../../src/remotion/ClipComposition.tsx";
import {
  appendEntry,
  applyEditState,
  emptyEditState,
  type EditPatch,
} from "../../../../src/lib/clipEditState.ts";
import { CANVAS_HEIGHT, CANVAS_WIDTH, FPS, type ClipProps } from "../../../../src/remotion/types.ts";
import { SAMPLE_DURATION_SEC } from "../../lib/onboardingSample";
import type { StepId } from "../../lib/brandSetup/steps";
import { SEED_ENTRY_LABEL } from "../../lib/brandSetup/keys";

/**
 * What the phone beside each step is showing (spec §2.2, each step's "Live").
 * Absent = cue 0, the one-line caption — the shot that answers "what do my
 * captions look like?" for every step that changes one.
 */
export const PHONE_SHOT: Partial<
  Record<StepId, { focusCue: 0 | 1 | 2 | null; seekSec?: number; expandable?: boolean; overlaysAlwaysOn?: boolean }>
> = {
  // The long sentence — the only cue that can show what "Large" costs you.
  captionSize: { focusCue: 2, expandable: true },
  // The card enters at 0.5 s and fades in over 18 frames (0.6 s), so 1.4 s is
  // the first moment it is FULLY opaque — the step that asks where the card
  // goes has to show the card, not a ghost of one (QA 2026-09-16, M2).
  speaker: { focusCue: null, seekSec: 1.4 },
  // The text step edits EVERY line on the video, and a reel's CTA is timed to
  // the last seconds of a real clip — off-screen on a six-second loop. The
  // preview pins every overlay to the whole loop (state untouched) so the
  // operator sees the line they are editing (round-2 §2.4).
  signoff: { focusCue: 0, overlaysAlwaysOn: true },
  review: { focusCue: 0, expandable: true, overlaysAlwaysOn: true },
};

export interface SamplePhoneProps {
  inputProps: ClipProps;
  /** Everything the geometry steps have decided so far. */
  designPatch?: EditPatch;
  /** Which sample cue this step is about — the player scrubs to it. */
  focusCue: 0 | 1 | 2 | null;
  /** A specific moment instead of a cue (the speaker card is at 0.5 s). */
  seekSec?: number;
  /** Phone height in px; the width follows from 9:16 and never exceeds the
   *  column (`min(width, 100%)`), so the page can't scroll sideways. */
  height: number;
  /** Offer "⤢ Full size" — used where the detail matters (caption size). */
  expandable?: boolean;
  /** Show every text overlay for the whole sample loop — PREVIEW only; the
   *  design's own timing is what ships (see `PHONE_SHOT.signoff`). */
  overlaysAlwaysOn?: boolean;
}

export function SamplePhone(p: SamplePhoneProps): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const playerRef = useRef<PlayerRef | null>(null);

  const effective = useMemo(() => {
    const patch = p.designPatch;
    const base =
      !patch || Object.keys(patch).length === 0
        ? p.inputProps
        : applyEditState(
            p.inputProps,
            appendEntry(emptyEditState(), { ts: 0, source: "inspector", label: SEED_ENTRY_LABEL, patch }),
          );
    const layout = base.layout;
    const overlays = layout?.overlays;
    if (!p.overlaysAlwaysOn || !layout || !overlays?.length) return base;
    const pinned: ClipProps = {
      ...base,
      layout: { ...layout, overlays: overlays.map((o) => ({ ...o, start: 0, durationSec: SAMPLE_DURATION_SEC })) },
    };
    return pinned;
  }, [p.inputProps, p.designPatch, p.overlaysAlwaysOn]);

  const seekFrame = useMemo(() => {
    if (p.seekSec !== undefined) return Math.round(p.seekSec * FPS);
    if (p.focusCue === null) return null;
    const cue = effective.cues[p.focusCue];
    if (!cue) return null;
    // +3 frames: far enough inside the cue that the caption is definitely on
    // screen, close enough that it is still the cue's own moment.
    return Math.round((cue.startMs / 1000) * FPS) + 3;
  }, [p.focusCue, p.seekSec, effective.cues]);

  const width = Math.round((p.height * 9) / 16);

  return (
    <div style={{ display: "grid", gap: 10, justifyItems: "center" }}>
      <PhoneFrame
        playerRef={playerRef}
        effective={effective}
        seekFrame={seekFrame}
        playing={playing}
        onPlayingChange={setPlaying}
        style={{ width: `min(${width}px, 100%)`, aspectRatio: "9 / 16" }}
      />

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
        <button
          type="button"
          onClick={() => (playing ? playerRef.current?.pause() : playerRef.current?.play())}
          title={playing ? "Pause" : "Play the sample"}
          style={ghostBtn}
        >
          {playing ? <Pause size={13} /> : <Play size={13} />}
          {playing ? "Pause" : "Play"}
        </button>
        {p.expandable && (
          <button type="button" onClick={() => setExpanded(true)} style={ghostBtn}>
            <Maximize2 size={13} />
            Full size
          </button>
        )}
      </div>

      <div style={{ fontSize: 12, color: "var(--ql-fg-subtle)", textAlign: "center", lineHeight: 1.5 }}>
        This is our sample. Your videos use your own footage.
      </div>

      {expanded && (
        <ExpandedPhone effective={effective} seekFrame={seekFrame} onClose={() => setExpanded(false)} />
      )}
    </div>
  );
}

const ghostBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "7px 13px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,.10)",
  background: "transparent",
  color: "var(--ql-fg-muted)",
  fontSize: 12.5,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};

function PhoneFrame(props: {
  playerRef: React.RefObject<PlayerRef | null>;
  effective: ClipProps;
  seekFrame: number | null;
  playing: boolean;
  onPlayingChange: (v: boolean) => void;
  style: React.CSSProperties;
}): React.JSX.Element {
  const { playerRef, effective, seekFrame, playing, onPlayingChange } = props;

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    const onPlay = (): void => onPlayingChange(true);
    const onPause = (): void => onPlayingChange(false);
    player.addEventListener("play", onPlay);
    player.addEventListener("pause", onPause);
    return () => {
      player.removeEventListener("play", onPlay);
      player.removeEventListener("pause", onPause);
    };
  }, [playerRef, onPlayingChange]);

  // Scrub to the step's cue — but never yank the playhead out from under a
  // running loop: ▶ means "show me the whole thing".
  //
  // The seek RETRIES, and that is the whole fix. The Player finishes mounting
  // a beat after this effect first runs and silently drops a `seekTo` sent
  // before then, so the speaker step — whose entire job is "the phone scrubs
  // to 0.6 s so the card is on screen while you pick its corner" — sat on
  // frame 0 showing a caption instead (QA 2026-09-16, M2). Asking again each
  // frame until the playhead is actually there costs nothing once it lands,
  // and is bounded so a player that never arrives cannot spin forever.
  useEffect(() => {
    if (seekFrame === null || playing) return;
    let raf = 0;
    let tries = 0;
    const apply = (): void => {
      const player = playerRef.current;
      if (player) {
        player.seekTo(seekFrame);
        if (player.getCurrentFrame() === seekFrame) return;
      }
      if (++tries > 90) return;
      raf = requestAnimationFrame(apply);
    };
    apply();
    return () => cancelAnimationFrame(raf);
  }, [seekFrame, playing, playerRef, effective]);

  return (
    <div
      style={{
        position: "relative",
        borderRadius: 20,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,.10)",
        background: "#000",
        boxShadow: "0 18px 50px rgba(0,0,0,.5)",
        ...props.style,
      }}
    >
      <Player
        ref={playerRef}
        component={ClipComposition}
        inputProps={effective}
        durationInFrames={SAMPLE_DURATION_SEC * FPS}
        fps={FPS}
        compositionWidth={CANVAS_WIDTH}
        compositionHeight={CANVAS_HEIGHT}
        style={{ width: "100%", height: "100%" }}
        clickToPlay={false}
        loop
        acknowledgeRemotionLicense
      />
    </div>
  );
}

function ExpandedPhone(props: {
  effective: ClipProps;
  seekFrame: number | null;
  onClose: () => void;
}): React.JSX.Element {
  const playerRef = useRef<PlayerRef | null>(null);
  const [playing, setPlaying] = useState(false);
  const { onClose } = props;

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-label="Full size preview"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "grid",
        placeItems: "center",
        background: "rgba(0,0,0,.82)",
        backdropFilter: "blur(6px)",
        padding: 16,
      }}
    >
      {/* The close button floats over the scrim rather than sitting under the
          phone: at `min(96vh, …)` there is no room beneath it, and a button
          below the fold is a button that does not exist. */}
      <button
        type="button"
        onClick={onClose}
        style={{ ...ghostBtn, position: "absolute", top: 16, right: 16, background: "rgba(0,0,0,.5)" }}
      >
        <X size={13} />
        Close
      </button>
      <div onClick={(e) => e.stopPropagation()} style={{ display: "grid", justifyItems: "center" }}>
        <PhoneFrame
          playerRef={playerRef}
          effective={props.effective}
          seekFrame={props.seekFrame}
          playing={playing}
          onPlayingChange={setPlaying}
          style={{ height: "min(96vh, 1200px)", maxWidth: "92vw", aspectRatio: "9 / 16" }}
        />
      </div>
    </div>
  );
}
